import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { busyDuring } from "../domain/freeBusy";
import { domainOf, isExternal, matchesPerson } from "../domain/people";
import { hm, minutesOfDay } from "../domain/time";
import { useAvailability, type LoadSchedules } from "../hooks/useAvailability";
import { useFrequentPeople } from "../hooks/useFrequentPeople";
import { usePeopleSearch } from "../hooks/usePeopleSearch";
import { EMAIL_RE, type Person } from "../services/people";
import { SearchIcon } from "./CalendarIcons";
import styles from "./CalendarModule.module.css";

/** Suggestions from your own meetings shown before "Show more results", as in Outlook. */
const SUGGESTED_SHOWN = 5;
/** Matches listed after "Show more results"; the list scrolls once it is taller than its box. */
const MATCHES_SHOWN = 10;

export function initials(name: string): string {
  const parts = name.trim().split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0] ?? "?").slice(0, 2).toUpperCase();
  return parts
    .slice(0, 2)
    .map((p) => p.charAt(0))
    .join("")
    .toUpperCase();
}

export type Availability = "free" | "busy" | "unknown";

export function availabilityOf(busy: boolean | null): Availability {
  if (busy === null) return "unknown";
  return busy ? "busy" : "free";
}

interface AttendeeInputProps {
  /** The meeting's time, to show whether each match is free then; null when no time is chosen yet. */
  readonly when: { readonly start: Date; readonly end: Date } | null;
  readonly token: string | null;
  /** Every address of your mailbox: you aren't suggested, and their domains decide who is "External". */
  readonly selfEmails: ReadonlySet<string>;
  /** Already on the invite (or you): not offered. */
  readonly exclude: ReadonlySet<string>;
  readonly loadSchedules: LoadSchedules;
  readonly onPick: (person: Person, availability: Availability) => void;
  readonly placeholder: string;
  /** Backspace in the empty box (the mockup's way of removing the last person added). */
  readonly onBackspaceEmpty?: () => void;
}

/** Suggestions first, then directory matches, leaving out anyone excluded or already listed; at most MATCHES_SHOWN. */
function mergeMatches(suggested: readonly Person[], results: readonly Person[], exclude: ReadonlySet<string>): Person[] {
  const merged = new Map<string, Person>();
  for (const p of [...suggested, ...results]) if (!exclude.has(p.email) && !merged.has(p.email)) merged.set(p.email, p);
  return [...merged.values()].slice(0, MATCHES_SHOWN);
}

/** The list when nothing matches what's typed: still searching, the directory couldn't be searched, or no one. */
function NoMatches({ searching, failed }: Readonly<{ searching: boolean; failed: boolean }>) {
  let text = "No one in the directory matches";
  if (searching) text = "Searching the directory…";
  else if (failed) text = "The directory can't be searched — type a full email address";
  return (
    <div className={styles.acList}>
      <div className={styles.acEmpty}>{text}</div>
    </div>
  );
}

/**
 * Adding people, the way Outlook's own picker works: from the first letter it
 * suggests the people you meet with most, and "Show more results" searches the
 * whole directory. Each match shows its email address, "External" when it is
 * outside your organisation, and whether they are free at the meeting's time.
 * If the directory can't be searched, a full email address can still be added.
 */
export function AttendeeInput({
  when,
  token,
  selfEmails,
  exclude,
  loadSchedules,
  onPick,
  placeholder,
  onBackspaceEmpty,
}: Readonly<AttendeeInputProps>) {
  const [query, setQuery] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const frequent = useFrequentPeople(token, selfEmails);
  const ownDomains = useMemo(() => new Set([...selfEmails].map(domainOf).filter(Boolean)), [selfEmails]);

  const typed = query.trim().toLowerCase();
  const suggested = typed
    ? frequent.filter((p) => !exclude.has(p.email) && matchesPerson(p, typed)).slice(0, SUGGESTED_SHOWN)
    : [];
  // The directory is searched once suggestions run out, or when asked for more.
  const searchDirectory = typed.length > 0 && (showMore || suggested.length === 0);
  const { results, failed, searching } = usePeopleSearch(token, query, searchDirectory);

  const people: Person[] = searchDirectory ? mergeMatches(suggested, results, exclude) : suggested;
  const typedEmail = EMAIL_RE.test(typed) && !exclude.has(typed) && !people.some((p) => p.email === typed);
  const options: Person[] = typedEmail ? [...people, { name: typed, email: typed }] : people;
  const canShowMore = !searchDirectory && suggested.length > 0;
  const itemCount = options.length + (canShowMore ? 1 : 0);

  const schedules = useAvailability(
    loadSchedules,
    options.map((p) => p.email),
    when?.start ?? new Date(0),
    when?.end ?? new Date(0),
    when !== null && options.length > 0,
  );
  const status = (email: string): Availability =>
    schedules && when ? availabilityOf(busyDuring(schedules, email, when, false)) : "unknown";
  const active = Math.min(highlight, Math.max(0, itemCount - 1));
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the highlighted row in view as the arrow keys move through a list that scrolls.
  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const pick = (person: Person) => {
    onPick(person, status(person.email));
    setQuery("");
    setShowMore(false);
    setHighlight(0);
  };
  const more = () => {
    setShowMore(true);
    setHighlight(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (itemCount) setHighlight((active + (e.key === "ArrowDown" ? 1 : itemCount - 1)) % itemCount);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const person = options[active];
      if (person) pick(person);
      else if (canShowMore) more();
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setQuery("");
      setShowMore(false);
    } else if (e.key === "Backspace" && query === "" && onBackspaceEmpty) {
      onBackspaceEmpty();
    }
  };

  const at = when ? hm(minutesOfDay(when.start)) : "";
  let list: ReactNode = null;
  if (itemCount > 0) {
    list = (
      <div ref={listRef} className={`${styles.acList} ${styles.acScroll}`} role="listbox" aria-label="Matching people">
        {options.map((p, i) => {
          const s = status(p.email);
          const external = isExternal(p.email, ownDomains);
          return (
            <button
              key={p.email}
              type="button"
              role="option"
              aria-selected={i === active}
              className={i === active ? styles.acHi : ""}
              // Keep focus in the input so the list doesn't close before the click lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(p)}
            >
              <span className={`${styles.av} ${styles.avSm}`}>{initials(p.name)}</span>
              <span className={styles.acPerson}>
                <span className={styles.acName}>
                  <span className={styles.acNameText}>{p.name}</span>
                  {external && <span className={styles.acExternal}>External</span>}
                </span>
                {p.name !== p.email && <span className={styles.acEmail}>{p.email}</span>}
              </span>
              {s !== "unknown" && (
                <span className={`${styles.acSub} ${s === "busy" ? styles.busyNote : ""}`}>
                  {s === "free" ? `Free at ${at}` : `Busy at ${at}`}
                </span>
              )}
            </button>
          );
        })}
        {canShowMore && (
          <button
            type="button"
            role="option"
            aria-selected={active === options.length}
            className={`${styles.acMore} ${active === options.length ? styles.acHi : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={more}
          >
            <SearchIcon />
            Show more results
          </button>
        )}
      </div>
    );
  } else if (typed) {
    list = <NoMatches searching={searching} failed={failed} />;
  }

  return (
    <div className={styles.ac}>
      <input
        className={styles.inp}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowMore(false);
          setHighlight(0);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={placeholder}
      />
      {list}
    </div>
  );
}
